import eyeMask1 from './recommendation_images/eye-mask-1.jpg';
import eyeMask2 from './recommendation_images/eye-mask-2.jpg';
import detoxMask1 from './recommendation_images/detox-mask-1.jpg';
import hydratingMask1 from './recommendation_images/hydrating-mask-1.jpg';
import lipMask1 from './recommendation_images/lip-mask-1.jpg';
import moisturizer1 from './recommendation_images/moisturizer-1.jpg';
// moisturizer-2.jpg exists in the directory but was mapped to id 'moisturizer-2'. 
// However, in the file, moisturizer-2 was using a placeholder. I should use the real image now.
import moisturizer2 from './recommendation_images/moisturizer-2.jpg';
import tool1 from './recommendation_images/tool-1.jpg';
import primer1 from './recommendation_images/primer-1.jpg';

export interface ProductLink {
    label: string;
    url: string;
    isAffiliate?: boolean;
    notes?: string; // e.g., "10% off"
}

export interface Product {
    id: string;
    name: string;
    brand?: string;
    category: string;
    image: string; // Placeholder or generated path
    description: string;
    priceRange: 'Budget' | 'Mid-Range' | 'Premium'; // Keep for sorting/filtering potential
    price: string; // Display price e.g. "$25"
    bestFor: string;
    pros: string[];
    cons: string[];
    rating: number; // 1-5
    links: ProductLink[];
    recommendationText: string; // Keeping for reference, but might not use in UI if requested to remove
}

export const categoryDescriptions: Record<string, string> = {
    'Under Eye Mask': 'The delicate skin under your eyes is often the first to show signs of stress and lack of sleep. A good eye mask hydrates, depuffs, and brightens, treating dark circles and ensuring your concealer sits perfectly.',
    'Detox Mask': 'Congested pores can lead to breakouts at the worst times. A detox mask helps draw out impurities and tighten pores, giving you a smooth, clean canvas for your wedding makeup.',
    'Hydrating Sheet Mask': 'Hydrated skin is glowing skin. Sheet masks force moisture into the skin, plumping fine lines and creating that "glass skin" effect that looks amazing in photos.',
    'Lip Mask': 'Dry, flaky lips can ruin a bridal look. Intensive lip masks exfoliate and deeply moisturize over time, ensuring your long-wear lipstick applies smoothly and stays comfortable.',
    'Moisturizer': 'The foundation of any skincare routine. finding the right moisturizer for your skin type balances oil production and maintains the protective barrier.',
    'Tools': 'Facial tools like ice rollers stimulate circulation and provide lymphatic drainage, sculpting the face and instantly waking up tired skin.',
    'Primer': 'Think of primer as the insurance policy for your makeup. It smooths texture, blurs pores, and grips your foundation so it lasts through tears, sweat, and dancing.',
    'Sunscreen': 'Protecting your skin is non-negotiable but you need one that avoids "flashback" (the white cast in photos). These picks protect without ruining your professional pictures.',
    'Hair Products': 'Your hair needs to stay fresh and voluminous. Dry shampoo is a lifesaver for adding texture and extending your style between washes.',
    'Hair Mask': 'Bridal hairstyles often require heat and product. Preparing your hair with deep conditioning masks ensures it remains shiny, soft, and resilient against styling damage.',
    'Silk Items': 'Cotton absorbs moisture and causes friction. Silk or satin accessories prevents sleep creases on your face and keeps your hair frizz-free and polished.',
};

export const bridesTopProducts: Product[] = [
    {
        id: 'eye-mask-1',
        category: 'Under Eye Mask',
        name: 'Forever Eye Mask',
        brand: 'Dieux',
        image: eyeMask1,
        description: 'Reusable 100% silicone patches. These ultra-thin masks hold gels, serums, and creams close to the skin for maximum absorption. A sustainable alternative to single-use patches.',
        priceRange: 'Mid-Range',
        price: '$50.00',
        bestFor: 'Sustainability & Absorption',
        pros: ['Reusable', 'Enhances any eye cream', 'Medical grade silicone'],
        cons: ['Requires separate serum'],
        rating: 4.8,
        links: [
            { label: 'Amazon', url: 'https://amzn.to/4jcZpIz' }
        ],
        recommendationText: 'We love that these reduce waste and can be used with your favorite eye cream.'
    },
    {
        id: 'eye-mask-2',
        category: 'Under Eye Mask',
        name: 'Water Drench Hyaluronic Cloud Hydra-Gel Eye Patches',
        brand: 'Peter Thomas Roth',
        image: eyeMask2,
        description: 'Hydrating eye patches with hyaluronic acid, collagen, and caffeine. Instantly depuffs and awakens tired eyes.',
        priceRange: 'Premium',
        price: '$55.00',
        bestFor: 'Puffiness & Hydration',
        pros: ['Instant cooling effect', 'Contains Hyaluronic Acid', 'Includes 60 patches'],
        cons: ['Higher price point'],
        rating: 4.7,
        links: [
            { label: 'Amazon', url: 'https://amzn.to/3Y7lezv' }
        ],
        recommendationText: 'A bridal emergency kit essential for puffy morning eyes.'
    },
    {
        id: 'detox-mask-1',
        category: 'Detox Mask',
        name: 'Instant Detox Mask',
        brand: 'Caudalie',
        image: detoxMask1,
        description: 'A pink clay mask that cleanses pores and removes impurities. Tightens pores in just 10 minutes.',
        priceRange: 'Mid-Range',
        price: '$42.00',
        bestFor: 'Congested Pores',
        pros: ['Visible pore tightening', 'Non-drying', 'Quick results (10 mins)'],
        cons: ['Small tube size'],
        rating: 4.6,
        links: [
            { label: 'Amazon', url: 'https://amzn.to/3LfA7gh', isAffiliate: true, notes: '10% off' }
        ],
        recommendationText: 'The viral mask that actually works to clear out pores before your big day.'
    },
    {
        id: 'hydrating-mask-1',
        category: 'Hydrating Sheet Mask',
        name: 'Bio-Collagen Real Deep Mask',
        brand: 'Biodance',
        image: hydratingMask1,
        description: 'A viral overnight mask that turns transparent as it absorbs into the skin. Delivers deep hydration and creates a glass-skin effect.',
        priceRange: 'Mid-Range',
        price: '$19.00',
        bestFor: 'Deep Hydration & Glow',
        pros: ['Overnight treatment', 'Glass skin finisher', 'Deeply moisturizing'],
        cons: ['Needs several hours to work'],
        rating: 4.5,
        links: [
            { label: 'Amazon', url: 'https://amzn.to/45mVZNw', isAffiliate: true, notes: '3% off' }
        ],
        recommendationText: 'Wake up with the most hydrated, glowing skin of your life.'
    },
    {
        id: 'lip-mask-1',
        category: 'Lip Mask',
        name: 'Lip Sleeping Mask',
        brand: 'Laneige',
        image: lipMask1,
        description: 'A leave-on lip mask that soothes and moisturizes for smoother, more supple lips overnight. Enriched with Vitamin C and antioxidants.',
        priceRange: 'Mid-Range',
        price: '$24.00',
        bestFor: 'Chapped Lips',
        pros: ['Smells amazing (Berry)', 'Lasts a long time', 'Very effective'],
        cons: ['Can be sticky'],
        rating: 4.8,
        links: [
            { label: 'Amazon', url: 'https://amzn.to/4s7f7ZR', isAffiliate: true, notes: '10% off' }
        ],
        recommendationText: 'Ensures your lips are perfectly smooth for your wedding day lipstick.'
    },
    {
        id: 'moisturizer-1',
        category: 'Moisturizer',
        name: 'Skin Conditioning Gel',
        brand: 'Naturie Hatomugi',
        image: moisturizer1,
        description: 'A lightweight, refreshing moisturizing gel that penetrates quickly. perfect for oily skin or hot climates as it hydrates without stickiness.',
        priceRange: 'Budget',
        price: '$13.63',
        bestFor: 'Oily Skin',
        pros: ['Very lightweight', 'Large volume', 'No sticky residue'],
        cons: ['Might not be enough for very dry skin'],
        rating: 4.5,
        links: [
            { label: 'Amazon', url: 'https://amzn.to/48R69bD' }
        ],
        recommendationText: 'An excellent lightweight option that wont make you greasy.'
    },
    {
        id: 'moisturizer-2',
        category: 'Moisturizer',
        name: 'Daily Moisturizing Lotion',
        brand: 'CeraVe',
        image: moisturizer2,
        description: 'Oil-free moisturizer with 3 essential ceramides and hyaluronic acid. Gentle, non-comedogenic, and perfect for sensitive skin.',
        priceRange: 'Budget',
        price: '$20.15',
        bestFor: 'Sensitive / Dry Skin',
        pros: ['Restores skin barrier', 'Fragrance-free', 'Dermatologist recommended'],
        cons: ['Basic packaging'],
        rating: 4.8,
        links: [
            { label: 'Amazon', url: 'https://amzn.to/3L8zrZY', isAffiliate: true, notes: '3% off' }
        ],
        recommendationText: 'A safe, reliable choice if your skin is sensitive or reactive.'
    },
    {
        id: 'tool-1',
        category: 'Tools',
        name: 'Ice Roller for Face',
        brand: 'Kitsch',
        image: tool1,
        description: 'Stainless steel ice roller to cool skin, reduce puffiness, and calm redness. Great for morning prep.',
        priceRange: 'Budget',
        price: '$17.99',
        bestFor: 'Depuffing & Soothing',
        pros: ['Stainless steel stays cold', 'Easy to sanitize', 'Instant relief'],
        cons: ['Needs freezer time'],
        rating: 4.6,
        links: [
            { label: 'Amazon', url: 'https://amzn.to/3Yrpn1z' }
        ],
        recommendationText: 'Quickly calms inflammation and wakes up your face.'
    },
    {
        id: 'primer-1',
        category: 'Primer',
        name: 'The POREfessional Pore Primer',
        brand: 'Benefit Cosmetics',
        image: primer1,
        description: 'A professional balm that minimizes the appearance of pores. Wraps skin in a silky, soft finish.',
        priceRange: 'Mid-Range',
        price: '$28.95',
        bestFor: 'Large Pores',
        pros: ['Silky texture', 'Matte finish', 'Visible smoothing'],
        cons: ['Silicone based'],
        rating: 4.7,
        links: [
            { label: 'Amazon', url: 'https://amzn.to/49wJl0Z' }
        ],
        recommendationText: 'The gold standard for blurring pores before makeup.'
    }
];
